import { readWebKeyboardInset } from '@shared-ui/platform/keyboard/web-keyboard-inset'
import { setWebSafeArea } from '@shared-ui/theme/web-safe-area'
import { isStandalonePwa } from '@web/libs/platform/pwa'
import { readWebEnvSafeAreaInsets, readWebSafeAreaInsets } from '@web/libs/safe-area'
import { getTelegramWebApp, getTmaBottomInset, getTmaTopInset, isTMA, isTMADesktop } from '@web/libs/tg/platform'

let lastTmaAppHeight: string | null = null
let lastMobileAppHeight: string | null = null

function isIOS(): boolean {
  // iPadOS 13+ может притворяться MacIntel — тогда смотрим maxTouchPoints.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function isMobileTouchDevice(): boolean {
  return isIOS() || window.matchMedia('(hover: none) and (pointer: coarse)').matches
}

// Обычная вкладка Safari, не иконка с домашнего экрана и не Telegram.
// В этой среде прокручивается документ, чтобы контент продолжался под панелями Safari.
function isIOSBrowserTab(): boolean {
  return isIOS() && !isStandalonePwa() && !(isTMA() && !isTMADesktop())
}

function readVisualViewportHeight(): number | null {
  const vv = window.visualViewport

  if (!vv || vv.offsetTop > 0) return null

  const height = Math.round(vv.height)
  return height > 0 ? height : null
}

function readAppHeight(): string {
  // Клавиатуру не ужимаем через --app-height: футер и контент двигаются через
  // KeyboardStickyView / KeyboardAwareScrollView (как на native). Иначе shell и sticky
  // footer конкурируют — футер «прыгает».

  // Telegram Mini App: 100dvh не всегда совпадает с фактической видимой областью webview
  // после expand/fullscreen/safe-area пересчётов. Для mobile TMA берём высоту из SDK.
  if (isTMA() && !isTMADesktop()) {
    if (readWebKeyboardInset() > 0 && lastTmaAppHeight) {
      return lastTmaAppHeight
    }

    const webApp = getTelegramWebApp()
    const height = webApp?.viewportStableHeight || webApp?.viewportHeight

    if (height && Number.isFinite(height) && height > 0) {
      lastTmaAppHeight = `${Math.round(height)}px`

      return lastTmaAppHeight
    }
  }

  // Standalone-PWA: браузерного UI нет, видимая высота == экрану. 100dvh/innerHeight на iOS
  // здесь занижены до первого скролла (→ отступ снизу), а статичный 100vh (lvh) не «дышит»
  // и сразу равен полному экрану.
  if (isStandalonePwa()) {
    return '100vh'
  }

  // Минимальная высота документа не меняется при сворачивании панелей Safari.
  if (isIOSBrowserTab()) {
    return '100lvh'
  }

  // Мобильный браузер: visualViewport точнее 100dvh (iOS + «Крупный текст» / Display Zoom).
  if (isMobileTouchDevice()) {
    if (readWebKeyboardInset() > 0 && lastMobileAppHeight) {
      return lastMobileAppHeight
    }

    const vvHeight = readVisualViewportHeight()

    if (vvHeight) {
      lastMobileAppHeight = `${vvHeight}px`
      return lastMobileAppHeight
    }
  }

  // Вкладка браузера (desktop / fallback): 100dvh под динамическую адресную строку.
  return '100dvh'
}

function syncViewport() {
  const iosBrowserTab = isIOSBrowserTab()

  document.documentElement.toggleAttribute('data-ios-browser', iosBrowserTab)
  document.documentElement.style.setProperty('--app-height', readAppHeight())

  // TMA: не перетираем store через env()/CSS (часто 0 до fullscreen) — берём SDK.
  // Иначе FooterSticky и прочие createStyleSheet на мгновение/навсегда остаются с bottom=0.
  if (isTMA() && !isTMADesktop()) {
    const top = getTmaTopInset()
    const bottom = getTmaBottomInset()

    setWebSafeArea({ top, bottom })
    document.documentElement.style.setProperty('--safe-area-inset-top', `${top}px`)
    document.documentElement.style.setProperty('--safe-area-inset-bottom', `${bottom}px`)
    return
  }

  if (!iosBrowserTab) {
    setWebSafeArea(readWebSafeAreaInsets())
    return
  }

  // Панели браузера перекрывают страницу, их высоту не добавляем к safe-area.
  const insets = readWebEnvSafeAreaInsets()

  setWebSafeArea(insets)
  document.documentElement.style.setProperty('--safe-area-inset-top', `${insets.top}px`)
  document.documentElement.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`)
}

export function installWebViewportSync() {
  if (typeof window === 'undefined') return

  syncViewport()

  requestAnimationFrame(() => {
    syncViewport()
    requestAnimationFrame(syncViewport)
  })

  window.addEventListener('load', syncViewport)
  window.addEventListener('pageshow', syncViewport)
  window.addEventListener('resize', syncViewport)
  window.addEventListener('orientationchange', syncViewport)
  window.visualViewport?.addEventListener('resize', syncViewport)
  window.visualViewport?.addEventListener('scroll', syncViewport)

  const webApp = getTelegramWebApp()

  if (isTMA() && !isTMADesktop() && webApp) {
    webApp.onEvent('viewportChanged', syncViewport)
    webApp.onEvent('fullscreenChanged', syncViewport)
    webApp.onEvent('safeAreaChanged', syncViewport)
    webApp.onEvent('contentSafeAreaChanged', syncViewport)
  }
}
